
import React from 'react';
import { OddsAnalysisResult } from '@/types/teamTypes';
import { OddsResultsTable } from './OddsResultsTable';
import { OddsAnalysisSummary } from './OddsAnalysisSummary';
import LoadingState from '@/components/football/LoadingState';
import ErrorDisplay from '@/components/common/ErrorDisplay';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface OddsAnalysisContentProps {
  homeOddsAnalysisData: OddsAnalysisResult | null;
  awayOddsAnalysisData: OddsAnalysisResult | null;
  matches: any[];
  isLoading: boolean;
  error: string | null;
  warning: string | null;
  selectedYears: string[];
  selectedTeam: string | null;
  selectedType: 'home' | 'away';
  teamLocation: 'any' | 'home' | 'away';
  onTypeChange: (type: 'home' | 'away') => void;
  onTeamLocationChange: (location: 'any' | 'home' | 'away') => void;
}

export const OddsAnalysisContent: React.FC<OddsAnalysisContentProps> = ({
  homeOddsAnalysisData,
  awayOddsAnalysisData,
  matches,
  isLoading,
  error,
  warning,
  selectedYears,
  selectedTeam,
  selectedType,
  teamLocation,
  onTypeChange,
  onTeamLocationChange
}) => {
  // Early return for loading state
  if (isLoading) {
    return <LoadingState compact />;
  }
  
  // Early return for error state
  if (error) {
    return <ErrorDisplay message={error} />;
  }
  
  // Early return for empty data state
  if (!homeOddsAnalysisData || !awayOddsAnalysisData) {
    return (
      <div className="text-center py-6 sm:py-8 text-gray-500">
        {selectedYears.length === 0 
          ? 'Select a country, league, and at least one season to view odds analysis' 
          : !warning ? 'Loading data...' : null}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {warning && (
        <Alert variant="default" className="mb-3 sm:mb-4 border-yellow-500 bg-yellow-50">
          <Info className="h-4 w-4 text-yellow-600" />
          <AlertTitle>Information</AlertTitle>
          <AlertDescription className="text-yellow-700">
            {warning}
          </AlertDescription>
        </Alert>
      )}
      
      <OddsAnalysisSummary 
        totalMatches={homeOddsAnalysisData.totalMatches} 
        selectedYears={selectedYears}
        selectedTeam={selectedTeam}
      />
      
      <div className="flex flex-wrap gap-4 mb-4 items-center justify-between">
        <Tabs 
          defaultValue={selectedType} 
          value={selectedType} 
          onValueChange={(value) => onTypeChange(value as 'home' | 'away')}
          className="w-auto"
        >
          <TabsList>
            <TabsTrigger value="home">Home Odds</TabsTrigger>
            <TabsTrigger value="away">Away Odds</TabsTrigger>
          </TabsList>
        </Tabs>

        {selectedTeam && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Team as:</span>
            <Select 
              value={teamLocation} 
              onValueChange={(value) => onTeamLocationChange(value as 'any' | 'home' | 'away')}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Team location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Home or Away</SelectItem>
                <SelectItem value="home">Home Only</SelectItem>
                <SelectItem value="away">Away Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      <OddsResultsTable 
        homeOddsRanges={homeOddsAnalysisData.ranges} 
        awayOddsRanges={awayOddsAnalysisData.ranges}
        matches={matches}
        selectedType={selectedType}
        onTypeChange={onTypeChange}
      />
    </div>
  );
};
